/**
 * Agent Registry Page (F031 P5)
 * Route: /agents | Nav: Model > Agent Registry
 *
 * The customer's AI agent estate governed as first-class assets — environment placement,
 * model/tools, risk tier, approval state, and MCP exposure. Built on the List & Grid
 * Standard — see `.github/context/list-grid-standard.context.md` and
 * `-behaviours.context.md`, following the reference migration in `src/pages/ApiKeysPage.tsx`.
 *
 * Binds to `AgentsController` (`api/portal/agents`). Register Agent / Edit Agent forms implement
 * the `FS-F031-AGENT` form-spec — see
 * `.specify/features/_active/FEA-F031-agent-registry/forms/AgentRegistration.form-spec.md` and
 * `src/features/agents/forms/`.
 *
 * RBAC deferred on the API (AgentsController TODO(RBAC) — no `RequireAnyRole` yet, standard
 * portal auth only) — actions here are ungated to match; do not invent roles.
 */
import { useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Text,
  Body2,
  Caption1,
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  Dropdown,
  Option,
  Spinner,
  Tooltip,
  Toaster,
  Toast,
  ToastTitle,
  ToastBody,
  useId,
  useToastController,
  makeStyles,
  tokens,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
} from '@fluentui/react-components';
import type { BadgeProps } from '@fluentui/react-components';
import {
  AddRegular,
  Edit24Regular,
  Bot24Regular,
  ChevronRightRegular,
  GaugeRegular,
  PlugDisconnected24Regular,
  CheckmarkCircle24Regular,
  Warning24Regular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentsApi } from '../services/api';
import type {
  Agent,
  AgentDetail,
  AgentEnvironment,
  AgentListFilter,
  AgentStatus,
  ApprovalState,
  RiskTier,
} from '../types';
import { AgentRegisterForm, AgentEditForm } from '../features/agents/forms';
import {
  ListPageLayout,
  MasterDetailHeader,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  useDebouncedValue,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { Crumb, ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';
import {
  DetailDrawer,
  DetailSection,
  DetailField,
  DetailMetadataGrid,
} from '../components/common/DetailDrawer';
import { MetricCard } from '../components/design-system';

// ---------------------------------------------------------------------------
// Enum option lists (verbatim C# enum member order)
// ---------------------------------------------------------------------------

const ENVIRONMENT_OPTIONS: AgentEnvironment[] = ['Development', 'Test', 'Staging', 'Production'];
const APPROVAL_STATE_OPTIONS: ApprovalState[] = ['NotRequired', 'Pending', 'Approved', 'Rejected', 'Expired'];
const RISK_TIER_OPTIONS: RiskTier[] = ['Unknown', 'Low', 'Medium', 'High', 'Critical'];
const STATUS_OPTIONS: AgentStatus[] = ['Draft', 'Active', 'Blocked', 'Retired', 'Ungoverned'];

// ---------------------------------------------------------------------------
// Badge color mapping — semantic map, not raw hex (per List & Grid Standard §15)
// ---------------------------------------------------------------------------

const riskTierColor = (tier: RiskTier): BadgeProps['color'] => {
  switch (tier) {
    case 'Critical': return 'danger';
    case 'High': return 'severe';
    case 'Medium': return 'warning';
    case 'Low': return 'success';
    case 'Unknown':
    default: return 'informative';
  }
};

/** Ordinal rank matching the C# `RiskTier` enum (Unknown=0 .. Critical=4) — the default
 *  sort comparator's `localeCompare` would otherwise sort risk tiers alphabetically
 *  (Critical, High, Low, Medium, Unknown), which is not severity order. */
const RISK_TIER_RANK: Record<RiskTier, number> = { Unknown: 0, Low: 1, Medium: 2, High: 3, Critical: 4 };

function getAgentSortValue(item: Agent, field: string): unknown {
  if (field === 'riskTier') return RISK_TIER_RANK[item.riskTier];
  return (item as unknown as Record<string, unknown>)[field];
}

const approvalStateColor = (state: ApprovalState): BadgeProps['color'] => {
  switch (state) {
    case 'Approved': return 'success';
    case 'Pending': return 'warning';
    case 'Rejected': return 'danger';
    case 'Expired': return 'severe';
    case 'NotRequired':
    default: return 'informative';
  }
};

const statusColor = (status: AgentStatus): BadgeProps['color'] => {
  switch (status) {
    case 'Active': return 'success';
    case 'Blocked': return 'danger';
    case 'Ungoverned': return 'severe';
    case 'Retired': return 'subtle';
    case 'Draft':
    default: return 'informative';
  }
};

// ---------------------------------------------------------------------------
// Summary panel constants — stat cards + agents-by-environment stacked bar.
// Colours are Fluent palette *token references* (CSS vars), never raw hex, so they pass
// lint:ux and honour the same semantics as the badge colour maps above
// (approved→green, pending→marigold/warning, rejected→red, expired→dark-orange/severe,
//  not-required→neutral).
// ---------------------------------------------------------------------------

/** Bounded sample size for the client-side summary aggregation. Matches the list grid's
 *  server clamp / adaptive threshold, so for any tenant at or below it the stat cards and the
 *  environment breakdown are exact; above it they reflect the first N (with an honest caption). */
const AGENT_SUMMARY_SAMPLE = 200; // mirrors AGENT_GRID_THRESHOLD (the server's skip/take clamp)

const STAT_ACCENT = {
  total: tokens.colorBrandForeground1,
  approved: tokens.colorPaletteGreenForeground1,
  ungoverned: tokens.colorPaletteRedForeground1,
  highRisk: tokens.colorPaletteDarkOrangeForeground1,
} as const;

const APPROVAL_SEGMENT_COLOR: Record<ApprovalState, string> = {
  Approved: tokens.colorPaletteGreenBackground3,
  Pending: tokens.colorPaletteMarigoldBackground3,
  Rejected: tokens.colorPaletteRedBackground3,
  Expired: tokens.colorPaletteDarkOrangeBackground3,
  NotRequired: tokens.colorNeutralBackground5,
};

/** An environment lacks an active approval when it is neither approved nor exempt. Used to flag
 *  the dangerous quadrant the spec calls out: production agents running without approval. */
const APPROVAL_STATES_LACKING_APPROVAL: ApprovalState[] = ['Pending', 'Rejected', 'Expired'];

// ---------------------------------------------------------------------------
// Filter state — 6 server-side facets, encoded into the shared `filters` URL param
// (§17/§19), `field:op:value` pairs joined by ';' — same convention as PolicyRunsPage.
// ---------------------------------------------------------------------------

interface AgentFilterState {
  environment: AgentEnvironment | '';
  approvalState: ApprovalState | '';
  minRiskTier: RiskTier | '';
  status: AgentStatus | '';
  mcpEnabled: '' | 'true' | 'false';
  ownerTeam: string;
}

const EMPTY_AGENT_FILTERS: AgentFilterState = {
  environment: '',
  approvalState: '',
  minRiskTier: '',
  status: '',
  mcpEnabled: '',
  ownerTeam: '',
};

function parseAgentFilters(raw: string): AgentFilterState {
  const state: AgentFilterState = { ...EMPTY_AGENT_FILTERS };
  if (!raw) return state;
  for (const part of raw.split(';')) {
    const [field, , ...rest] = part.split(':');
    const value = rest.join(':');
    if (!field || !value) continue;
    switch (field) {
      case 'environment': state.environment = value as AgentEnvironment; break;
      case 'approvalState': state.approvalState = value as ApprovalState; break;
      case 'minRiskTier': state.minRiskTier = value as RiskTier; break;
      case 'status': state.status = value as AgentStatus; break;
      case 'mcpEnabled': state.mcpEnabled = value as 'true' | 'false'; break;
      case 'ownerTeam': state.ownerTeam = value; break;
      default: break;
    }
  }
  return state;
}

function serializeAgentFilters(state: AgentFilterState): string {
  const parts: string[] = [];
  if (state.environment) parts.push(`environment:eq:${state.environment}`);
  if (state.approvalState) parts.push(`approvalState:eq:${state.approvalState}`);
  if (state.minRiskTier) parts.push(`minRiskTier:eq:${state.minRiskTier}`);
  if (state.status) parts.push(`status:eq:${state.status}`);
  if (state.mcpEnabled) parts.push(`mcpEnabled:eq:${state.mcpEnabled}`);
  if (state.ownerTeam) parts.push(`ownerTeam:eq:${state.ownerTeam}`);
  return parts.join(';');
}

function toAgentListFilter(state: AgentFilterState): AgentListFilter {
  return {
    environment: state.environment || undefined,
    approvalState: state.approvalState || undefined,
    minRiskTier: state.minRiskTier || undefined,
    status: state.status || undefined,
    mcpEnabled: state.mcpEnabled ? state.mcpEnabled === 'true' : undefined,
    ownerTeam: state.ownerTeam || undefined,
  };
}

/**
 * The Agent Registry API is real server-side pagination (skip/take, clamped 1-200 by
 * `AgentsController.List`) — unlike several sibling pages that wrap an unpaged/capped
 * endpoint. `threshold` is set to match the server's clamp exactly, so the adaptive
 * grid's client/server split never straddles a partially-clamped page (see
 * `.github/context/list-grid-standard-behaviours.context.md` §10's "never filter/sort/
 * search a partial set" invariant).
 */
const AGENT_GRID_THRESHOLD = 200;

async function fetchAgentsPage(filter: AgentListFilter, params: ListQueryParams): Promise<PagedResult<Agent>> {
  const take = params.pageSize;
  const skip = (params.page - 1) * take;
  const result = await agentsApi.getAll(filter, skip, take);
  const totalPages = Math.max(Math.ceil(result.totalCount / take), 1);
  return {
    items: result.items,
    totalCount: result.totalCount,
    pageNumber: params.page,
    pageSize: take,
    totalPages,
    hasPreviousPage: params.page > 1,
    hasNextPage: params.page < totalPages,
  };
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalL,
  },
  shadowAiCard: {
    flexShrink: 0,
  },
  shadowAiBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: `0 ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL}`,
  },
  shadowAiRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  shadowAiRowMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  shadowAiEmpty: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  ownerTeamInput: {
    minWidth: '10rem',
  },
  filterDropdown: {
    minWidth: '9rem',
  },
  // rem, not a spacing token — this caps the Register/Edit Agent dialog surface height so a
  // 14-field form scrolls internally instead of growing the page (List & Grid Standard §-adjacent
  // pattern for long forms in a Dialog).
  formDialogSurface: {
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  subRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  subRowHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  subList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  // --- Summary panel (stat cards + agents-by-environment stacked bar) ---
  summarySection: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  statCardRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  envChart: {
    flexShrink: 0,
  },
  envChartBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: `0 ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL}`,
  },
  envBarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  envBarLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: '7rem',
    flexShrink: 0,
  },
  envBarTrack: {
    flexGrow: 1,
    height: '1.25rem', // fixed bar height — no spacing token above 24px (per ux-consistency §rem fallback)
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    overflow: 'hidden',
    display: 'flex',
  },
  envBarFill: {
    display: 'flex',
    height: '100%',
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  envBarSegment: {
    height: '100%',
    minWidth: '2px', // keep a hairline visible for a single-agent segment
  },
  envDangerBadge: {
    flexShrink: 0,
  },
  chartLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalXS,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  legendSwatch: {
    width: '0.75rem',
    height: '0.75rem',
    borderRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
  },
  cappedNote: {
    color: tokens.colorNeutralForeground3,
  },
});

const crumbs: Crumb[] = [{ label: 'Agent Registry' }];

// ---------------------------------------------------------------------------
// AgentSummaryPanel — header stat cards + agents-by-environment stacked bar (F031 §8.1).
// Aggregates are computed client-side from one bounded list fetch (AGENT_SUMMARY_SAMPLE).
// The ungoverned count reuses ShadowAiPanel's ['agents-ungoverned'] query key so React Query
// serves it from cache (no duplicate fetch). Total is always exact (server totalCount); the
// approval/risk breakdown is exact when totalCount <= sample, otherwise labelled "first N of M"
// (F043: absence of full data is stated, never presented as a clean verdict).
// ---------------------------------------------------------------------------

const AgentSummaryPanel: FC = () => {
  const styles = useStyles();
  const summary = useQuery({
    queryKey: ['agents-summary', AGENT_SUMMARY_SAMPLE],
    queryFn: () => agentsApi.getAll({}, 0, AGENT_SUMMARY_SAMPLE),
  });
  // Same key as ShadowAiPanel — deduped by React Query, so the shadow-AI sweep runs once.
  const ungoverned = useQuery({
    queryKey: ['agents-ungoverned'],
    queryFn: () => agentsApi.getUngoverned(),
  });

  if (summary.isLoading) {
    return (
      <div className={styles.summarySection}>
        <LoadingSkeleton rows={2} columns={4} />
      </div>
    );
  }
  if (summary.isError || !summary.data) {
    return (
      <div className={styles.summarySection}>
        <ErrorState message="Unable to load the agent summary." onRetry={() => summary.refetch()} />
      </div>
    );
  }

  const { items, totalCount } = summary.data;
  const capped = totalCount > items.length;
  const approvedCount = items.filter((a) => a.approvalState === 'Approved').length;
  const highRiskCount = items.filter((a) => a.riskTier === 'High' || a.riskTier === 'Critical').length;

  const detectionAvailable = ungoverned.data?.detectionAvailable ?? false;
  const ungovernedCount = ungoverned.data?.observedCount ?? 0;

  const buckets = ENVIRONMENT_OPTIONS.map((env) => {
    const rows = items.filter((a) => a.environment === env);
    const byApproval = APPROVAL_STATE_OPTIONS.reduce<Record<ApprovalState, number>>((acc, s) => {
      acc[s] = rows.filter((a) => a.approvalState === s).length;
      return acc;
    }, { NotRequired: 0, Pending: 0, Approved: 0, Rejected: 0, Expired: 0 });
    const lackingApproval =
      env === 'Production'
        ? rows.filter((a) => APPROVAL_STATES_LACKING_APPROVAL.includes(a.approvalState)).length
        : 0;
    return { env, total: rows.length, byApproval, lackingApproval };
  });
  const maxEnvTotal = Math.max(1, ...buckets.map((b) => b.total));

  return (
    <div className={styles.summarySection}>
      <div className={styles.statCardRow}>
        <MetricCard title="Total Agents" value={totalCount} icon={<Bot24Regular />} accentColor={STAT_ACCENT.total} />
        <MetricCard
          title="Approved"
          value={capped ? `${approvedCount}+` : approvedCount}
          icon={<CheckmarkCircle24Regular />}
          accentColor={STAT_ACCENT.approved}
        />
        <MetricCard
          title="Ungoverned (Shadow AI)"
          value={detectionAvailable ? ungovernedCount : '—'}
          icon={<PlugDisconnected24Regular />}
          accentColor={STAT_ACCENT.ungoverned}
          secondaryMetrics={[{ label: 'Detection', value: detectionAvailable ? 'Active' : 'No source connected' }]}
        />
        <MetricCard
          title="High-Risk"
          value={capped ? `${highRiskCount}+` : highRiskCount}
          icon={<Warning24Regular />}
          accentColor={STAT_ACCENT.highRisk}
          secondaryMetrics={[{ label: 'Tiers', value: 'High + Critical' }]}
        />
      </div>

      <Card className={styles.envChart}>
        <CardHeader
          header={<Text weight="semibold">Agents by Environment</Text>}
          description={<Caption1>Split by approval state — watch the production + not-approved quadrant.</Caption1>}
        />
        <div className={styles.envChartBody}>
          {totalCount === 0 ? (
            <Caption1>No agents registered yet.</Caption1>
          ) : (
            <>
              {buckets.map((b) => (
                <div key={b.env} className={styles.envBarRow}>
                  <div className={styles.envBarLabel}>
                    <Body2>{b.env}</Body2>
                    <Caption1>{b.total} {b.total === 1 ? 'agent' : 'agents'}</Caption1>
                  </div>
                  <div className={styles.envBarTrack}>
                    <div className={styles.envBarFill} style={{ width: `${(b.total / maxEnvTotal) * 100}%` }}>
                      {APPROVAL_STATE_OPTIONS.filter((s) => b.byApproval[s] > 0).map((s) => (
                        <Tooltip key={s} content={`${s}: ${b.byApproval[s]}`} relationship="label">
                          <div
                            className={styles.envBarSegment}
                            style={{ flexGrow: b.byApproval[s], backgroundColor: APPROVAL_SEGMENT_COLOR[s] }}
                            aria-label={`${b.env} — ${s}: ${b.byApproval[s]}`}
                          />
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                  {b.lackingApproval > 0 && (
                    <Badge className={styles.envDangerBadge} appearance="tint" color="danger">
                      {b.lackingApproval} unapproved in production
                    </Badge>
                  )}
                </div>
              ))}
              <div className={styles.chartLegend}>
                {APPROVAL_STATE_OPTIONS.map((s) => (
                  <div key={s} className={styles.legendItem}>
                    <span className={styles.legendSwatch} style={{ backgroundColor: APPROVAL_SEGMENT_COLOR[s] }} />
                    <Caption1>{s}</Caption1>
                  </div>
                ))}
              </div>
              {capped && (
                <Caption1 className={styles.cappedNote}>
                  Approval and risk breakdown reflects the first {items.length} of {totalCount} agents.
                </Caption1>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ShadowAiPanel — F031 P4 shadow-AI sweep (§ card, sibling above the list frame)
// ---------------------------------------------------------------------------

const ShadowAiPanel: FC = () => {
  const styles = useStyles();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['agents-ungoverned'],
    queryFn: () => agentsApi.getUngoverned(),
  });

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div className={styles.shadowAiEmpty}>
        <Spinner size="tiny" />
        <Body2>Checking for ungoverned agents…</Body2>
      </div>
    );
  } else if (isError) {
    body = (
      <div className={styles.shadowAiEmpty}>
        <Warning24Regular />
        <Body2>Unable to load the shadow-AI sweep.</Body2>
        <Button appearance="secondary" size="small" onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  } else if (!data?.detectionAvailable) {
    // detectionAvailable: false means no telemetry source is connected — surface "connect a
    // source", NOT a false "0 ungoverned" (F043 zero-denominator guard).
    body = (
      <div className={styles.shadowAiEmpty}>
        <PlugDisconnected24Regular />
        <Body2>
          No telemetry source connected. Connect a connector (GitHub Actions, Azure AI Foundry,
          an MCP gateway, or an LLM-provider org usage feed) to enable shadow-AI detection.
        </Body2>
      </div>
    );
  } else if (data.ungoverned.length === 0) {
    body = (
      <div className={styles.shadowAiEmpty}>
        <CheckmarkCircle24Regular style={{ color: tokens.colorPaletteGreenForeground1 }} />
        <Body2>No ungoverned agents detected across {data.observedCount} observed agent(s).</Body2>
      </div>
    );
  } else {
    body = (
      <div className={styles.subList}>
        {data.ungoverned.map((u) => (
          <div key={u.observedAgentKey} className={styles.shadowAiRow}>
            <div className={styles.shadowAiRowMain}>
              <Text weight="semibold">{u.displayName}</Text>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                Source: {u.source}
                {u.observedEnvironment ? ` · ${u.observedEnvironment}` : ''} · First seen{' '}
                {new Date(u.firstSeen).toLocaleDateString()} · Last seen{' '}
                {new Date(u.lastSeen).toLocaleDateString()}
              </Caption1>
            </div>
            <Badge appearance="tint" color={riskTierColor(u.assumedRiskTier)}>
              {u.assumedRiskTier}
            </Badge>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Card className={styles.shadowAiCard}>
      <CardHeader
        image={<Warning24Regular style={{ color: tokens.colorPaletteRedForeground1 }} />}
        header={<Text weight="semibold">Shadow AI Sweep</Text>}
        description={<Caption1>Agents observed in telemetry but absent from the registry</Caption1>}
      />
      <div className={styles.shadowAiBody}>{body}</div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// AgentDetailDrawer — full AgentDetailDto (every field the DTO exposes)
// ---------------------------------------------------------------------------

const AgentDetailDrawer: FC<{ agentId: number | null; onClose: () => void }> = ({ agentId, onClose }) => {
  const styles = useStyles();
  const { data, isLoading, isError } = useQuery<AgentDetail>({
    queryKey: ['agent-detail', agentId],
    queryFn: () => agentsApi.getById(agentId!),
    enabled: agentId !== null,
  });

  return (
    <DetailDrawer open={agentId !== null} onClose={onClose} title="Agent Details" size="large">
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
          <Spinner label="Loading agent…" />
        </div>
      )}
      {isError && <ErrorState message="Unable to load this agent's detail." />}
      {data && (
        <>
          <DetailSection title={data.agent.agentName}>
            <Body2>{data.agent.purpose}</Body2>
            <DetailMetadataGrid>
              <DetailField label="Environment">
                <Badge appearance="outline">{data.agent.environment}</Badge>
              </DetailField>
              <DetailField label="Status">
                <Badge appearance="tint" color={statusColor(data.agent.status)}>{data.agent.status}</Badge>
              </DetailField>
              <DetailField label="Approval State">
                <Badge appearance="tint" color={approvalStateColor(data.agent.approvalState)}>
                  {data.agent.approvalState}
                </Badge>
              </DetailField>
              <DetailField label="Risk Tier">
                <Badge appearance="tint" color={riskTierColor(data.agent.riskTier)}>{data.agent.riskTier}</Badge>
              </DetailField>
              <DetailField label="Owner">
                <Text>{data.agent.ownerId}{data.agent.ownerTeam ? ` (${data.agent.ownerTeam})` : ''}</Text>
              </DetailField>
              <DetailField label="Origin">
                <Text>{data.agent.origin}</Text>
              </DetailField>
              <DetailField label="Model">
                <Text>{data.agent.modelProvider} / {data.agent.modelName}</Text>
              </DetailField>
              <DetailField label="Runtime Provider">
                <Text>{data.agent.runtimeProvider ?? '—'}</Text>
              </DetailField>
              <DetailField label="Current Version">
                <Text>{data.agent.agentVersion}</Text>
              </DetailField>
              <DetailField label="Data Access Level">
                <Text>{data.agent.dataAccessLevel}</Text>
              </DetailField>
              <DetailField label="Tool Access Level">
                <Text>{data.agent.toolAccessLevel}</Text>
              </DetailField>
              <DetailField label="MCP Enabled">
                <Badge appearance="tint" color={data.agent.mcpEnabled ? 'success' : 'subtle'}>
                  {data.agent.mcpEnabled ? 'Yes' : 'No'}
                </Badge>
              </DetailField>
              <DetailField label="Created">
                <Text>{new Date(data.agent.createdAt).toLocaleString()}</Text>
              </DetailField>
              <DetailField label="Updated">
                <Text>{data.agent.updatedAt ? new Date(data.agent.updatedAt).toLocaleString() : '—'}</Text>
              </DetailField>
              <DetailField label="External ID">
                <Text style={{ fontFamily: 'monospace' }}>{data.agent.externalId}</Text>
              </DetailField>
              <DetailField label="Governance Graph Node">
                <Text style={{ fontFamily: 'monospace' }}>{data.graphNodeExternalId}</Text>
              </DetailField>
            </DetailMetadataGrid>
          </DetailSection>

          <DetailSection title={`Versions (${data.versions.length})`} divider>
            {data.versions.length === 0 ? (
              <Caption1>None recorded.</Caption1>
            ) : (
              <div className={styles.subList}>
                {data.versions.map((v) => (
                  <div key={v.id} className={styles.subRow}>
                    <div className={styles.subRowHeader}>
                      <Text weight="semibold">{v.versionLabel}</Text>
                      <Badge appearance="tint" color={v.isCurrent ? 'success' : 'subtle'}>
                        {v.isCurrent ? 'Current' : 'Retired'}
                      </Badge>
                    </div>
                    <Caption1>{v.modelProvider} / {v.modelName}</Caption1>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                      Created {new Date(v.createdAt).toLocaleString()}
                      {v.retiredAt ? ` · Retired ${new Date(v.retiredAt).toLocaleString()}` : ''}
                    </Caption1>
                  </div>
                ))}
              </div>
            )}
          </DetailSection>

          <DetailSection title={`Capabilities (${data.capabilities.length})`} divider>
            {data.capabilities.length === 0 ? (
              <Caption1>None recorded.</Caption1>
            ) : (
              <div className={styles.subList}>
                {data.capabilities.map((c) => (
                  <div key={c.id} className={styles.subRow}>
                    <Text weight="semibold">{c.name}</Text>
                    {c.description && <Caption1>{c.description}</Caption1>}
                  </div>
                ))}
              </div>
            )}
          </DetailSection>

          <DetailSection title={`Tool Bindings (${data.toolBindings.length})`} divider>
            {data.toolBindings.length === 0 ? (
              <Caption1>None recorded.</Caption1>
            ) : (
              <div className={styles.subList}>
                {data.toolBindings.map((t) => (
                  <div key={t.id} className={styles.subRow}>
                    <div className={styles.subRowHeader}>
                      <Text style={{ fontFamily: 'monospace' }}>{t.toolExternalId}</Text>
                      <Badge appearance="outline">{t.mode}</Badge>
                    </div>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                      {t.viaMcp ? `Via MCP server ${t.mcpServerExternalId ?? '—'}` : 'Native tool binding'}
                    </Caption1>
                  </div>
                ))}
              </div>
            )}
          </DetailSection>

          <DetailSection title={`Policy Bindings (${data.policyBindings.length})`} divider>
            {data.policyBindings.length === 0 ? (
              <Caption1>None recorded.</Caption1>
            ) : (
              <div className={styles.subList}>
                {data.policyBindings.map((p) => (
                  <div key={p.id} className={styles.subRow}>
                    <Text style={{ fontFamily: 'monospace' }}>{p.policyExternalId}</Text>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Policy ID: {p.policyId}</Caption1>
                  </div>
                ))}
              </div>
            )}
          </DetailSection>

          <DetailSection title={`Runtime Bindings (${data.runtimeBindings.length})`} divider>
            {data.runtimeBindings.length === 0 ? (
              <Caption1>None recorded.</Caption1>
            ) : (
              <div className={styles.subList}>
                {data.runtimeBindings.map((r) => (
                  <div key={r.id} className={styles.subRow}>
                    <Text weight="semibold">{r.runtimeProvider}</Text>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                      {r.runtimeRef ? `Ref: ${r.runtimeRef}` : 'No runtime ref'}
                      {r.environmentRef ? ` · Environment node: ${r.environmentRef}` : ''}
                    </Caption1>
                  </div>
                ))}
              </div>
            )}
          </DetailSection>

          <DetailSection title={`Approvals (${data.approvals.length})`} divider>
            {data.approvals.length === 0 ? (
              <Caption1>None recorded.</Caption1>
            ) : (
              <div className={styles.subList}>
                {data.approvals.map((a) => (
                  <div key={a.id} className={styles.subRow}>
                    <div className={styles.subRowHeader}>
                      <Badge appearance="tint" color={a.outcome === 'Approved' ? 'success' : 'danger'}>
                        {a.outcome}
                      </Badge>
                      <Badge appearance="outline">{a.approvedAtTier}</Badge>
                    </div>
                    <Caption1>By {a.approverId} on {new Date(a.decidedAt).toLocaleString()}</Caption1>
                    {a.note && <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{a.note}</Caption1>}
                    {a.expiresAt && (
                      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                        Expires {new Date(a.expiresAt).toLocaleString()}
                      </Caption1>
                    )}
                  </div>
                ))}
              </div>
            )}
          </DetailSection>
        </>
      )}
    </DetailDrawer>
  );
};

// ---------------------------------------------------------------------------
// AgentRegistryPage
// ---------------------------------------------------------------------------

export const AgentRegistryPage: FC = () => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const toasterId = useId('agent-registry-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);

  // Register Agent / Edit Agent dialogs (FS-F031-AGENT). B-04 — confirm on dismiss when dirty.
  const [isRegisterDialogOpen, setIsRegisterDialogOpen] = useState(false);
  const [registerDirty, setRegisterDirty] = useState(false);
  const [editAgentId, setEditAgentId] = useState<number | null>(null);
  const [editDirty, setEditDirty] = useState(false);

  const closeRegisterDialog = (force = false) => {
    if (!force && registerDirty && !window.confirm('Discard unsaved changes?')) return;
    setIsRegisterDialogOpen(false);
    setRegisterDirty(false);
  };

  const closeEditDialog = (force = false) => {
    if (!force && editDirty && !window.confirm('Discard unsaved changes?')) return;
    setEditAgentId(null);
    setEditDirty(false);
  };

  const handleAgentRegistered = (agent: Agent) => {
    queryClient.invalidateQueries({ queryKey: ['agents-grid'] });
    dispatchToast(
      <Toast>
        <ToastTitle>Agent Registered</ToastTitle>
        <ToastBody>{agent.agentName} has been added to the registry.</ToastBody>
      </Toast>,
      { intent: 'success', timeout: 5000 }
    );
    closeRegisterDialog(true);
  };

  const handleAgentUpdated = (agent: Agent) => {
    queryClient.invalidateQueries({ queryKey: ['agents-grid'] });
    queryClient.invalidateQueries({ queryKey: ['agent-detail', agent.id] });
    dispatchToast(
      <Toast>
        <ToastTitle>Agent Updated</ToastTitle>
        <ToastBody>{agent.agentName} has been updated.</ToastBody>
      </Toast>,
      { intent: 'success', timeout: 5000 }
    );
    closeEditDialog(true);
  };

  const { state, setPage, setSort, setSearch, setFilters } = useListUrlState({ defaultSort: 'agentName:asc' });

  const filterState = parseAgentFilters(state.filters);
  const agentListFilter = toAgentListFilter(filterState);

  const [ownerTeamInput, setOwnerTeamInput] = useState(filterState.ownerTeam);
  useEffect(() => {
    setOwnerTeamInput(filterState.ownerTeam);
    // Re-sync only when the URL's ownerTeam filter changes externally (e.g. back/forward).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState.ownerTeam]);
  const debouncedOwnerTeam = useDebouncedValue(ownerTeamInput, 300);
  useEffect(() => {
    if (debouncedOwnerTeam === filterState.ownerTeam) return;
    setFilters(serializeAgentFilters({ ...filterState, ownerTeam: debouncedOwnerTeam }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOwnerTeam]);

  const updateFilter = <K extends keyof AgentFilterState>(key: K, value: AgentFilterState[K]) =>
    setFilters(serializeAgentFilters({ ...filterState, [key]: value }));

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const grid = useAdaptiveGridData<Agent>({
    // The filters string is folded INTO queryKeyBase (not passed as `filtersSerialized`) because
    // these facets are applied SERVER-SIDE by `AgentsController` — a filter change must produce a
    // fresh probe/page fetch, not an in-memory `clientPredicate` re-filter of an already-filtered set.
    queryKeyBase: ['agents-grid', state.filters],
    fetchPage: (params) => fetchAgentsPage(agentListFilter, params),
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    // Search has no server-side counterpart on this endpoint — it only takes effect in adaptive
    // client mode (total <= AGENT_GRID_THRESHOLD), scanning the already server-filtered rows.
    searchFields: ['agentName', 'purpose', 'ownerTeam', 'modelName'],
    sortState,
    getValue: getAgentSortValue,
    threshold: AGENT_GRID_THRESHOLD,
  });

  const {
    items: agents,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = grid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after filter/search.
  useEffect(() => {
    if (!isLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [isLoading, state.page, totalPages, setPage]);

  const evaluateMutation = useMutation({
    mutationFn: (id: number) => agentsApi.evaluate(id),
    onSuccess: (risk) => {
      queryClient.invalidateQueries({ queryKey: ['agents-grid'] });
      queryClient.invalidateQueries({ queryKey: ['agent-detail', risk.agentId] });
      const guardNote = risk.productionGuard.passes
        ? 'Production placement guard: passes.'
        : `Production placement guard: blocked — ${risk.productionGuard.blockingReason ?? 'see agent detail'}.`;
      dispatchToast(
        <Toast>
          <ToastTitle>Risk Evaluated: {risk.riskTier}</ToastTitle>
          <ToastBody>
            {risk.contributors.length > 0 ? risk.contributors.join('; ') : 'No contributing factors recorded.'}
            {' '}
            {guardNote}
          </ToastBody>
        </Toast>,
        { intent: risk.productionGuard.passes ? 'success' : 'warning', timeout: 6000 }
      );
    },
  });

  const actionButtonCount = 3; // Evaluate + Edit + detail chevron — RBAC deferred, ungated for all users.
  const columnCount = 8; // name, environment, riskTier, approvalState, status, ownerTeam, mcpEnabled, actions

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={6} columns={columnCount} />;
  } else if (isError) {
    listBody = <ErrorState message="Unable to load the agent registry." onRetry={refetch} />;
  } else if (agents.length === 0) {
    listBody = (
      <EmptyState
        icon={<Bot24Regular />}
        title="No Agents Registered"
        description="No agents match the current filters, or no agents have been registered yet for this tenant."
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="Agent registry">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'agentName' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('agentName')}
                onClick={() => toggleSort('agentName')}
                style={{ width: '100%' }}
              >
                Name
              </TableHeaderCell>
              <TableHeaderCell>Environment</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'riskTier' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('riskTier')}
                onClick={() => toggleSort('riskTier')}
              >
                Risk Tier
              </TableHeaderCell>
              <TableHeaderCell>Approval</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Owner Team</TableHeaderCell>
              <TableHeaderCell>MCP</TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agents.map((item) => {
              const isEvaluating = evaluateMutation.isPending && evaluateMutation.variables === item.id;
              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <div>
                      <Text weight="semibold">{item.agentName}</Text>
                      <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                        {item.modelProvider} / {item.modelName} · v{item.agentVersion}
                      </Caption1>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="outline">{item.environment}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={riskTierColor(item.riskTier)}>{item.riskTier}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={approvalStateColor(item.approvalState)}>
                      {item.approvalState}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={statusColor(item.status)}>{item.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <Body2>{item.ownerTeam ?? '—'}</Body2>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={item.mcpEnabled ? 'success' : 'subtle'}>
                      {item.mcpEnabled ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                      <Tooltip content="Evaluate Risk" relationship="label">
                        <Button
                          appearance="subtle"
                          icon={<GaugeRegular />}
                          size="small"
                          aria-label="Evaluate risk"
                          disabled={isEvaluating}
                          onClick={() => evaluateMutation.mutate(item.id)}
                        />
                      </Tooltip>
                      <Tooltip content="Edit Agent" relationship="label">
                        <Button
                          appearance="subtle"
                          icon={<Edit24Regular />}
                          size="small"
                          aria-label="Edit agent"
                          onClick={() => setEditAgentId(item.id)}
                        />
                      </Tooltip>
                      <Button
                        appearance="subtle"
                        icon={<ChevronRightRegular />}
                        size="small"
                        aria-label="View agent details"
                        onClick={() => setSelectedAgentId(item.id)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.page}>
      <Toaster toasterId={toasterId} position="top-end" />

      {/* Header stat cards + agents-by-environment stacked bar (F031 §8.1). */}
      <AgentSummaryPanel />

      {/* Shadow-AI sweep — sibling above the list frame, non-scrolling. */}
      <ShadowAiPanel />

      <div className={styles.listSection}>
        <ListPageLayout
          header={
            <MasterDetailHeader
              crumbs={crumbs}
              busy={isFetching && !isLoading}
              subtitle="The customer's AI agent estate, governed as first-class assets"
            />
          }
          toolbar={
            <ListToolbar
              primaryActions={
                <Button appearance="primary" icon={<AddRegular />} onClick={() => setIsRegisterDialogOpen(true)}>
                  Register Agent
                </Button>
              }
              search={state.search}
              onSearchChange={setSearch}
              searchPlaceholder="Search agents…"
              extraFilters={
                <>
                  <Dropdown
                    className={styles.filterDropdown}
                    placeholder="Environment"
                    value={filterState.environment}
                    onOptionSelect={(_, data) =>
                      updateFilter('environment', (data.optionValue || '') as AgentEnvironment | '')
                    }
                  >
                    <Option value="">All Environments</Option>
                    {ENVIRONMENT_OPTIONS.map((v) => <Option key={v} value={v}>{v}</Option>)}
                  </Dropdown>
                  <Dropdown
                    className={styles.filterDropdown}
                    placeholder="Approval State"
                    value={filterState.approvalState}
                    onOptionSelect={(_, data) =>
                      updateFilter('approvalState', (data.optionValue || '') as ApprovalState | '')
                    }
                  >
                    <Option value="">All Approval States</Option>
                    {APPROVAL_STATE_OPTIONS.map((v) => <Option key={v} value={v}>{v}</Option>)}
                  </Dropdown>
                  <Dropdown
                    className={styles.filterDropdown}
                    placeholder="Min Risk Tier"
                    value={filterState.minRiskTier}
                    onOptionSelect={(_, data) =>
                      updateFilter('minRiskTier', (data.optionValue || '') as RiskTier | '')
                    }
                  >
                    <Option value="">Any Risk Tier</Option>
                    {RISK_TIER_OPTIONS.map((v) => <Option key={v} value={v}>{v}</Option>)}
                  </Dropdown>
                  <Dropdown
                    className={styles.filterDropdown}
                    placeholder="Status"
                    value={filterState.status}
                    onOptionSelect={(_, data) =>
                      updateFilter('status', (data.optionValue || '') as AgentStatus | '')
                    }
                  >
                    <Option value="">All Statuses</Option>
                    {STATUS_OPTIONS.map((v) => <Option key={v} value={v}>{v}</Option>)}
                  </Dropdown>
                  <Dropdown
                    className={styles.filterDropdown}
                    placeholder="MCP Enabled"
                    value={filterState.mcpEnabled === '' ? '' : filterState.mcpEnabled === 'true' ? 'Yes' : 'No'}
                    onOptionSelect={(_, data) =>
                      updateFilter(
                        'mcpEnabled',
                        data.optionValue === 'Yes' ? 'true' : data.optionValue === 'No' ? 'false' : ''
                      )
                    }
                  >
                    <Option value="">MCP: Any</Option>
                    <Option value="Yes">MCP: Yes</Option>
                    <Option value="No">MCP: No</Option>
                  </Dropdown>
                  <Input
                    className={styles.ownerTeamInput}
                    placeholder="Owner Team"
                    value={ownerTeamInput}
                    onChange={(_, data) => setOwnerTeamInput(data.value)}
                  />
                </>
              }
              onRefresh={refetch}
              isRefreshing={isFetching}
              testIdPrefix="agent-registry"
            />
          }
          footer={
            <Pager
              page={state.page}
              totalPages={totalPages}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
              totalCount={totalCount}
              onPageChange={setPage}
              testId="agent-registry-pager"
            />
          }
        >
          {listBody}
        </ListPageLayout>
      </div>

      <AgentDetailDrawer agentId={selectedAgentId} onClose={() => setSelectedAgentId(null)} />

      {/* Register Agent (FS-F031-AGENT A-01) */}
      <Dialog
        open={isRegisterDialogOpen}
        onOpenChange={(_, data) => (data.open ? setIsRegisterDialogOpen(true) : closeRegisterDialog())}
      >
        <DialogSurface className={styles.formDialogSurface}>
          <DialogTitle>Register Agent</DialogTitle>
          <DialogBody>
            <DialogContent>
              <AgentRegisterForm
                onSuccess={handleAgentRegistered}
                onCancel={() => closeRegisterDialog()}
                onDirtyChange={setRegisterDirty}
              />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Edit Agent (FS-F031-AGENT A-02) */}
      <Dialog
        open={editAgentId !== null}
        onOpenChange={(_, data) => {
          if (data.open) return;
          closeEditDialog();
        }}
      >
        <DialogSurface className={styles.formDialogSurface}>
          <DialogTitle>Edit Agent</DialogTitle>
          <DialogBody>
            <DialogContent>
              {editAgentId !== null && (
                <AgentEditForm
                  agentId={editAgentId}
                  onSuccess={handleAgentUpdated}
                  onCancel={() => closeEditDialog()}
                  onDirtyChange={setEditDirty}
                />
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
